import TableDataModel from "./TableDataModel";
import {
  Button,
  Center,
  Group,
  Loader,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import SearchBox from "../components/SearchBox";
import { modals } from "@mantine/modals";
import TableItemsCount from "../components/TableItemsCount";
import { ApiResponseDataType } from "../common/utilities";
import LoadMoreItems from "../components/LoadMoreItems";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import {
  createTableItem,
  deleteTableItem,
  fetchNextInstructorSno,
  fetchTableItems,
  updateTableItem,
  exportInstructorsCsv,
} from "../api/dynamodb-db-api";
import saveAs from "file-saver";
import CreateUpdateTableItem from "../components/CreateUpdateNewTableItem";
import TableComponent from "./TableComponent";
import { IconPlus, IconFileExport } from "@tabler/icons-react";
import ApiListResponse from "../common/ApiListResponse";

export default function TablePageComponent({
  tableData,
}: {
  tableData: TableDataModel;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [apiResponse, setApiResponse] = useState<ApiListResponse | undefined>(
    undefined
  );
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const searchAttributeValue =
    searchParams.get(tableData.searchAttribute.name) ?? undefined;

  // 🔹 Generic fetch function for all tables
  async function fetchTablePage() {
    setLoadingMore(true);

    // 🔹 Use listPath for instructor, apiPath for others
    const apiPath = tableData.name.singular === "instructor" 
      ? tableData.listPath! 
      : tableData.apiPath;

    const tableRes = await fetchTableItems(
      apiPath,
      tableData.searchAttribute.name,
      searchAttributeValue,
      searchAttributeValue ? undefined : apiResponse?.meta?.lastEvaluatedKeyValue
    );

    if (tableRes instanceof Error) {
      setApiResponse({
        data: apiResponse?.data,
        meta: { lastEvaluatedKeyValue: apiResponse?.meta.lastEvaluatedKeyValue },
        error: tableRes,
      });
      setLoadingMore(false);
      return;
    }

    // 🔹 When searching, replace data; when loading more, append
    setApiResponse({
      data: searchAttributeValue
        ? tableRes.data
        : [...(apiResponse?.data ?? []), ...tableRes.data],
      meta: { lastEvaluatedKeyValue: tableRes.meta.lastEvaluatedKeyValue },
      error: undefined,
    });

    setLoadingMore(false);
  }

  // 🔹 Remove item
  async function remove(item: ApiResponseDataType) {
    const response = await deleteTableItem(
      tableData.apiPath,
      Object.fromEntries(
        tableData.primaryAttributes.map((a) => [a.name, item[a.name]])
      )
    );
    if (response instanceof Error) {
      notifications.show({
        title: "Failed to Delete",
        message: response.message,
        color: "orange",
      });
    } else {
      setApiResponse({
        data: [...(apiResponse?.data ?? []).filter((i) => i !== item)],
        meta: { lastEvaluatedKeyValue: apiResponse?.meta?.lastEvaluatedKeyValue },
        error: undefined,
      });
      notifications.show({
        title: `${tableData.label.singular} Deleted`,
        message: `${tableData.label.singular} ${item[tableData.primaryAttribute!.name]} has been deleted`,
        color: "red",
      });
    }
  }

  // 🔹 Fetch data when search params change
  useEffect(() => {
    // 🔹 Reset data when search changes
    setApiResponse(undefined);
    fetchTablePage().then();
  }, [searchParams]);

  return (
    <Stack>
      <Group align={"center"} justify={"space-between"}>
        <Group>
          <SearchBox searchAttribute={tableData.searchAttribute} />
          <Space w="xs" />
          <Button
            variant="light"
            leftSection={<IconPlus size={18} />}
            onClick={async () => {
              let defaultValues: Record<string, any> = {};

              if (tableData.name.singular === "instructor") {
                try {
                  const nextSnoResponse = await fetchNextInstructorSno(
                    tableData.nextSnoPath!
                  );
                  defaultValues.sno = nextSnoResponse.data;
                } catch (err) {
                  defaultValues.sno = "";
                }
                // 🔹 Set default hired date to today
                defaultValues.hiredDate = new Date().toISOString().split('T')[0];
              }

              modals.open({
                id: `create-${tableData.name.singular}`,
                title: `Create a New ${tableData.label.singular}`,
                centered: true,
                children: (
                  <CreateUpdateTableItem
                    attributes={tableData.attributes}
                    defaultValues={defaultValues}
                    onSubmit={async (newItem) => {
                      const result = await createTableItem(
                        tableData.apiPath,
                        newItem
                      );
                      if (result instanceof Error) return result.message;

                      notifications.show({
                        title: "New Item Created",
                        message: `${tableData.label.singular} ${newItem[tableData.primaryAttribute!.name]} has been created`,
                        color: "blue",
                      });
                      
                      // 🔹 Clear search and refresh to show new item
                      setSearchParams({});
                      setApiResponse(undefined);
                      fetchTablePage();

                      return undefined;
                    }}
                  />
                ),
              });
            }}
          >
            <Text>Add New Item</Text>
          </Button>
        </Group>
        <Group gap="sm" align="center">
          {tableData.name.singular === "instructor" && (
            <Button
              variant="outline"
              leftSection={<IconFileExport size={16} />}
              onClick={async () => {
                try {
                  const res = await exportInstructorsCsv(tableData.apiPath);
                  if (res instanceof Error) {
                    notifications.show({
                      title: "Export Failed",
                      message: (res as Error).message,
                      color: "orange",
                    });
                    return;
                  }

                  const blob = new Blob([res], { type: "text/csv;charset=utf-8;" });
                  const fileName = `instructors_${new Date().toISOString().split("T")[0]}.csv`;
                  saveAs(blob, fileName);
                } catch (err) {
                  notifications.show({
                    title: "Export Error",
                    message: "Failed to download CSV",
                    color: "red",
                  });
                }
              }}
            >
              Export CSV
            </Button>
          )}

          <TableItemsCount
            key={tableData.name.plural}
            label={tableData.label.plural}
            apiPath={tableData.apiPath}
          />
        </Group>
      </Group>

      <TableComponent
        tableAttributes={tableData.attributes}
        editable={tableData.editable}
        deletable={tableData.deletable}
        values={apiResponse?.data}
        onRemove={remove}
        onEdit={(item) => {
          modals.open({
            id: `update-${tableData.name.singular}`,
            title: `Update ${tableData.label.singular} Entry`,
            centered: true,
            children: (
              <CreateUpdateTableItem
                attributes={tableData.attributes}
                currentItem={item}
                onSubmit={async (newItem) => {
                  const result = await updateTableItem(
                    tableData.apiPath,
                    newItem
                  );
                  if (result instanceof Error) return result.message;
                  
                  notifications.show({
                    title: "Item Updated",
                    message: `${tableData.label.singular} ${newItem[tableData.primaryAttribute!.name]} has been updated`,
                    color: "blue",
                  });
                  
                  // 🔹 Refresh the current view
                  setApiResponse(undefined);
                  fetchTablePage().then();
                  
                  return undefined;
                }}
              />
            ),
          });
        }}
        onOpenRelated={(attribute, value) =>
          navigate(
            `/tables/${attribute.linkedTable}?${attribute.linkedAttributeName}=${value}`
          )
        }
      />

      {apiResponse?.error && (
        <Group>
          <Text>{apiResponse.error.message}</Text>
        </Group>
      )}
      {apiResponse?.data === undefined && (
        <Group>
          <Center>
            <Loader />
          </Center>
        </Group>
      )}
      {apiResponse?.data && apiResponse.data.length === 0 && (
        <Group>
          <Text>No results</Text>
        </Group>
      )}
      {apiResponse?.meta.lastEvaluatedKeyValue && (
        <Center>
          <LoadMoreItems loading={loadingMore} onClick={fetchTablePage} />
        </Center>
      )}
    </Stack>
  );
}